import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Measurement } from "../../../../../../../models/measurement";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Status, MeasurementTypes } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import MeasurementModal from "./MeasurementModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const MeasurementsPage = ({ permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const {
    measurements,
    page,
    pageSize,
    total,
    hasErrors,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    updateMeasurement,
    createMeasurement,
  } = useActions();

  const [selectedMeasurements, setSelectedMeasurements] = useState<Measurement>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`${classes.statusBadge} ${
        status === Status.ACTIVE ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const editMeasurement = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    const findMeasurement = measurements.find(
      (measurement) => Number(measurement.id) === Number(index)
    );

    if (findMeasurement) {
      setSelectedMeasurements(findMeasurement);
      setOpenModal(true);
    }
  };

  const changeStatusMeasurement = (index: number) => {
    const findMeasurement = measurements.find(
      (measurement) => Number(measurement.id) === Number(index)
    );

    if (findMeasurement) {
      updateMeasurement({
        id: Number(index),
        measurement: {
          ...findMeasurement,
          status: findMeasurement.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          data-cy="changeStatus"
          customColor={Color.INFO}
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveMeasurementSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, measurements);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "code", title: translate("code") },
    { name: "type", hideSearch: true, title: translate("type") },
    {
      name: "description",
      title: translate("name"),
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission.create)
      ? {
        text: translate("create"),
        action: () => {
          if (hasErrors !== undefined) setHasErrors(undefined);
          setSelectedMeasurements(undefined);
          setOpenModal(true);
        },
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        {
          name: translate("type"),
          column: "type",
          options: [MeasurementTypes.DIMENSIONS, MeasurementTypes.WEIGHT],
        },
        {
          name: translate("description"),
          column: "description",
        },
        {
          name: translate("code"),
          column: "code",
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderButtonToChangeStatus(),
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.measurements",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="measurementDataList">
      <ShipeeziNewGrid
        data={measurements}
        columns={columns}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (measureId) => editMeasurement(Number(measureId))
            : undefined,
          handleChange: (measureId) => changeStatusMeasurement(Number(measureId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveMeasurementSelected(rows)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openModal && (
        <MeasurementModal
          measurement={selectedMeasurements}
          open={openModal}
          hasError={hasErrors}
          isLoading={isLoading}
          closeModal={() => setOpenModal(false)}
          submit={(measurement: any) =>
            selectedMeasurements
              ? selectedMeasurements.id &&
                updateMeasurement({
                  id: selectedMeasurements.id,
                  measurement: { ...selectedMeasurements, ...measurement },
                })
              : createMeasurement({
                  ...measurement,
                  companyId: currentUser?.companyId,
                  valid: true,
                })
          }
        />
      )}
      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateMeasurement({
                id: rowId,
                measurement: { status } as Measurement,
              });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default MeasurementsPage;
